import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getLoggedInParticipant } from '../api/participantAuth';
import WelcomeBanner from '../components/WelcomeBanner';
import './RaceLandingPage.css';

const RaceLandingPage = () => {
  const history = useHistory();

  // Dashboard cards configuration - prioritized by usage frequency
  const dashboardCards = [
    // {
    //   id: 'birds',
    //   title: 'View Bird List',
    //   description: 'View all birds entered in the race including breeder, handler, and status.',
    //   icon: 'bi-list-ul',
    //   route: 'birds',
    //   priority: 1,
    //   cta: 'Browse Bird List'
    // }, 
    {
      id: 'participants',
      title: 'View Participants',
      description: 'Browse breeders and handlers participating in the race.',
      icon: 'bi-people-fill',
      route: 'participants',
      priority: 1,
      cta: 'Browse Participants'
    },
    // {
    //   id: 'self-service',
    //   title: 'Login/Self Service Portal',
    //   description: 'Update your profile, manage birds, and review handler requests.',
    //   icon: 'bi-gear-fill',
    //   route: 'self-service',
    //   priority: 2,
    //   cta: 'Access Portal'
    // },
    {
      id: 'details',
      title: 'View Race Details',
      description: 'View race rules, schedule, release location, and prize information.',
      icon: 'bi-info-circle-fill',
      route: 'details',
      priority: 3,
      cta: 'Learn More'
    },
    // {
    //   id: 'registration',
    //   title: 'Registration / Sign Up',
    //   description: 'Register as a breeder or handler for the 2026 Convention Race.',
    //   icon: 'bi-person-plus-fill',
    //   route: 'registration',
    //   priority: 4,
    //   cta: 'Sign Up Now',
    //   featured: true
    // }
  ];

  const handleRoute = (route) => {
    switch (route) {
      case 'details':
        history.push('/race/details');
        break;
      case 'participants':
        history.push('/race/participants');
        break;
      case 'birds':
        history.push('/race/birds');
        break;
      case 'registration':
        history.push('/race/signup');
        break;
      case 'self-service': {
        const auth = getLoggedInParticipant();
        const isValidSession = auth?.participantId && (!auth.expiresAt || new Date(auth.expiresAt) > new Date());
        history.push(isValidSession ? '/race/dashboard' : '/race/select-login');
        break;
      }
      default:
        break;
    }
  };

  // Group cards by priority for layout
  const priority1Cards = dashboardCards.filter(card => card.priority === 1);
  const priority2Cards = dashboardCards.filter(card => card.priority === 2);
  const priority3Cards = dashboardCards.filter(card => card.priority === 3);
  const priority4Cards = dashboardCards.filter(card => card.priority === 4);

  const renderCard = (card) => (
    <div key={card.id} className={`col-lg-6 col-md-6`}>
      <div 
        className={`route-card ${card.featured ? 'featured' : ''}`}
        onClick={() => handleRoute(card.route)}
      >
        {card.featured && (
          <div className="featured-badge">
            <i className="bi bi-star-fill"></i>
            <span>Get Started</span>
          </div>
        )}
        <div className="route-icon">
          <i className={`bi ${card.icon}`}></i>
        </div>
        <h3>{card.title}</h3>
        <p>{card.description}</p>
        <div className={`route-action ${card.featured ? 'primary' : ''}`}>
          <span>{card.cta}</span>
          <i className="bi bi-arrow-right"></i>
        </div>
      </div>
    </div>
  );

  return (
    <div className="race-landing-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <div className="hero-content">
                <h1 className="hero-title">
                  2026 International Federation Convention Race</h1> 
                  <h2>Hosted By Central Jersey Combine</h2> 
                
                <p className="hero-subtitle">
                  Join pigeon racing enthusiasts from around the nation in the premier convention race event
                </p>
                <div className="hero-stats">
                  <div className="stat-item">
                    <span className="stat-number">300+</span>
                    <span className="stat-label">Miles</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">20,000 POINTS</span>
                    <span className="stat-label">FIRST PRIZE (Based on entries)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logged In User Welcome Banner */}
      <WelcomeBanner />

      {/* Navigation Cards Section */}
      <div className="navigation-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="section-header text-center mb-5">
                <h2>What would you like to do?</h2>
                <p className="text-muted">Choose your path to get started with the 2026 Convention Race</p>
              </div>
              
              {/* Priority 1 Row - Top priority items (2 columns) */}
              <div className="row g-4 mb-4">
                {priority1Cards.map(card => renderCard(card))}
              </div>

              {/* Priority 2 & 3 Row (2 columns) */}
              <div className="row g-4 mb-4">
                {priority2Cards.map(card => renderCard(card))}
                {priority3Cards.map(card => renderCard(card))}
              </div>

              {/* Priority 4 Row - Registration (spans full width, appears as col-lg-6) */}
              <div className="row g-4">
                {priority4Cards.map(card => renderCard(card))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Dates Section */}
      <div className="py-5" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-center mb-5">
                <h2 className="fw-bold mb-3" style={{ color: '#0b3d91' }}>
                  <i className="bi bi-calendar-event me-3"></i>
                  Important Dates
                </h2>
                <p className="text-muted fs-5">Mark your calendar for these key milestones</p>
              </div>
              
              <div className="row g-4">
                <div className="col-md-4">
                  <div className="card h-100 border-0 shadow-sm hover-lift" style={{ transition: 'transform 0.2s ease' }}>
                    <div className="card-body text-center p-4">
                      <div className="mb-3">
                        <i className="bi bi-calendar-plus-fill fs-1 text-dark"></i>
                      </div>
                      <h5 className="fw-bold mb-2">Start accepting birds</h5>
                      <div className="display-6 fw-bold text-dark mb-2">March 15 2026</div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="card h-100 border-0 shadow-sm hover-lift" style={{ transition: 'transform 0.2s ease' }}>
                    <div className="card-body text-center p-4">
                      <div className="mb-3">
                        <i className="bi bi-calendar-x-fill fs-1 text-dark"></i>
                      </div>
                      <h5 className="fw-bold mb-2">Ends accepting birds</h5>
                      <div className="display-6 fw-bold text-dark mb-2">May 15, 2026</div>
                      <p className="text-muted mb-1">(Replacements until June 1)</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-4">
                  <div className="card h-100 border-0 shadow-sm hover-lift" style={{ transition: 'transform 0.2s ease' }}>
                    <div className="card-body text-center p-4">
                      <div className="mb-3">
                        <i className="bi bi-flag-fill fs-1 text-dark"></i>
                      </div>
                      <h5 className="fw-bold mb-2">Race Day</h5>
                      <div className="display-6 fw-bold text-dark mb-2">Shipping: Nov5 (Thursday) - Liberation: Nov6 (Friday)</div>
                      <p className="text-muted mb-1">The big day!</p>
                      <small className="text-muted">(Weather permitting)</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaceLandingPage;